import { BaseElement } from 'base';
import { bus } from 'bus';
import { cart } from 'cart';
import { cartGifts } from 'cart-gifts';
import { isPlainClick } from 'is-plain-click';
import { marketNotice, MARKET_NOTICE_TOPIC } from 'market-notice';
import { shopTheLookRequest } from 'shop-the-look-request';
import { SLIDE_CART_TOPIC, CART_DRAWER_ID } from 'slide-cart-topic';

const CHECKOUT = 'action-button.cart-drawer__checkout';
const CHECKOUT_RESET_MS = 10000;
const NOTICE = 'inline-message.cart-drawer__market-notice';
const NOTICE_DWELL_MS = 500;
const NOTICE_RATIO = 0.5;

const lineOf = element => element.closest('[data-line-key]');
const lineKey = element => lineOf(element)?.dataset.lineKey;
const giftTier = element =>
  element.closest('[data-gift-tier]')?.dataset.giftTier;

const keyList = value => (value ?? '').split(' ').filter(Boolean);

const dropKeys = (line, quantity) =>
  keyList(line?.dataset.giftDrop)
    .map(entry => entry.split('@'))
    .filter(([minKeep, key]) => key && quantity < Number(minKeep))
    .map(([, key]) => key);

const setQuantity = (element, quantity) => {
  const line = lineOf(element);
  const id = line?.dataset.lineKey;
  const gifts = new Set([
    ...(quantity === 0 ? keyList(line?.dataset.giftKeys) : []),
    ...dropKeys(line, quantity)
  ]);
  if (!gifts.size) return cart.change({ id, quantity });
  return cart.update({
    updates: Object.fromEntries([
      [id, quantity],
      ...[...gifts].map(key => [key, 0])
    ])
  });
};

const removeLine = element => setQuantity(element, 0);

const settleGifts = async task => {
  const result = await task();
  await cartGifts.idle();
  return result;
};

class CartDrawer extends BaseElement {
  #busy = 0;
  #stale = false;
  #open = false;
  #bound = new WeakSet();
  #pending = new Map();
  #checkoutTimer = null;
  #refreshing = false;
  #notice = false;
  #noticeTarget = null;
  #noticeTimer = null;
  #noticeObserver = null;
  #lookId = null;

  #controls = [
    {
      tag: 'qty-counter',
      selector: 'qty-counter.cart-drawer__counter',
      bind: counter =>
        counter.run(change =>
          this.#mutate(() =>
            settleGifts(() => setQuantity(counter, change.value))
          )
        ),
      lock: (counter, on) => counter.setDisabled(on)
    },
    this.#linkControl('link-button.cart-drawer__remove', link =>
      settleGifts(() => removeLine(link))
    ),
    {
      tag: 'select-field',
      selector: 'select-field.cart-drawer__gift-select',
      bind: select =>
        this.cleanup(
          select.onChange(({ value }) =>
            this.#mutate(() =>
              cartGifts.choose({
                tier: giftTier(select),
                handle: value,
                lineKey: lineKey(select)
              })
            ).catch(() => {})
          )
        )
    },
    this.#linkControl('link-button.cart-drawer__gift-remove', link =>
      cartGifts.dismiss({ tier: giftTier(link), lineKey: lineKey(link) })
    ),
    this.#linkControl('link-button.cart-drawer__gift-restore', link =>
      cartGifts.restore({ tier: giftTier(link) })
    ),
    {
      tag: 'upsell-carousel',
      selector: 'upsell-carousel',
      bind: carousel =>
        carousel.run(request =>
          request.type === 'options'
            ? this.#requestLook(request)
            : this.#mutate(() => cart.add({ id: request.variantId }), {
                notify: false
              })
        )
    },
    {
      tag: 'discount-field',
      selector: 'discount-field',
      bind: field =>
        field.run(input =>
          this.#mutate(
            () =>
              settleGifts(() =>
                input.type === 'apply'
                  ? cart.applyDiscount(input)
                  : cart.removeDiscount(input)
              ),
            { notify: false }
          )
        )
    },
    {
      tag: 'action-button',
      selector: CHECKOUT,
      bind: () => {},
      lock: (button, on) => {
        button.setDisabled(on);
        button.setLoading(on);
      }
    },
    {
      tag: 'inline-message',
      selector: NOTICE,
      bind: () => this.#watchNotice()
    }
  ];

  onConnect() {
    this.#bound = new WeakSet();
    this.#pending = new Map();

    if (this.dataset.market)
      cartGifts.start({
        config: this.#giftConfig(),
        market: this.dataset.market
      });
    this.#noticeObserver = new IntersectionObserver(
      entries => this.#onNoticeIntersect(entries),
      { threshold: NOTICE_RATIO }
    );
    this.cleanup(() => {
      this.#noticeObserver.disconnect();
      clearTimeout(this.#noticeTimer);
      this.#noticeTarget = null;
    });

    this.subscribe(SLIDE_CART_TOPIC, this.#onActive);
    this.cleanup(() => this.#cancelLook());
    this.subscribe(MARKET_NOTICE_TOPIC, active => {
      this.#notice = active === true;
      this.#watchNotice();
    });
    this.subscribeLive('cart:updated', ({ sections } = {}) => {
      if (sections) this.#stale = false;
      marketNotice.settle();
    });
    this.observe(
      new MutationObserver(() => {
        this.#bind();
        this.#watchNotice();
      }),
      this,
      { childList: true, subtree: true }
    );
    this.listen(this, 'error', this.#onMediaError, true);
    this.listen(this, 'click', this.#onClick);
    this.listen(document, 'visibilitychange', () => {
      if (document.visibilityState === 'visible') this.#markStale();
    });
    this.listen(window, 'pageshow', this.#onPageShow);
    this.cleanup(() => clearTimeout(this.#checkoutTimer));

    this.qa('.slide-cart-card__img').forEach(img => {
      if (img.complete && img.currentSrc && img.naturalWidth === 0)
        img.hidden = true;
    });
    this.#bind();
  }

  #linkControl(selector, task) {
    return {
      tag: 'link-button',
      selector,
      bind: link =>
        link.run(() => {
          if (this.#busy) return;
          this.#mutate(() => task(link)).catch(() => {});
        }),
      lock: (link, on) => link.setDisabled(on)
    };
  }

  #giftConfig() {
    const script = this.q('script#cart-gift-config');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[cart-drawer] gift config is not valid JSON:', error);
      return null;
    }
  }

  #bind() {
    this.#controls.forEach(control => this.#bindControl(control));
  }

  #bindControl(control) {
    if (!customElements.get(control.tag)) {
      if (this.#pending.has(control.tag)) return;
      this.#pending.set(
        control.tag,
        customElements.whenDefined(control.tag).then(() => {
          this.#pending.delete(control.tag);
          if (this.isConnected) this.#bind();
        })
      );
      return;
    }
    for (const el of this.qa(control.selector)) {
      if (this.#bound.has(el)) continue;
      this.#bound.add(el);
      control.bind(el);
      if (this.#busy > 0) control.lock?.(el, true);
    }
  }

  #setBusy(delta) {
    const was = this.#busy > 0;
    this.#busy = Math.max(0, this.#busy + delta);
    const on = this.#busy > 0;
    if (on === was) return;
    for (const control of this.#controls) {
      if (!control.lock) continue;
      this.#boundAll(control.selector).forEach(el => control.lock(el, on));
    }
  }

  #boundAll(selector) {
    return this.qa(selector).filter(el => this.#bound.has(el));
  }

  async #mutate(task, { notify = true } = {}) {
    this.#setBusy(1);
    try {
      return await task();
    } catch (error) {
      console.error('[cart-drawer] cart update failed:', error);
      if (notify && this.isConnected)
        bus.publish('toast:raised', {
          id: crypto.randomUUID(),
          text: this.dataset.error
        });
      throw error;
    } finally {
      this.#setBusy(-1);
    }
  }

  #onActive(active) {
    this.#open = active === CART_DRAWER_ID;
    if (!this.#open) this.#cancelLook();
    if (this.#open && this.#stale) this.#refresh();
    this.#watchNotice();
  }

  #requestLook({ productId, url }) {
    const { id, settled } = shopTheLookRequest.open({ productId, url });
    this.#lookId = id;
    return settled;
  }

  #cancelLook() {
    shopTheLookRequest.cancel(this.#lookId);
    this.#lookId = null;
  }

  #watchNotice() {
    queueMicrotask(() => {
      if (!this.isConnected) return;
      const target =
        this.#open && this.#notice && !marketNotice.seen
          ? (this.qa(NOTICE).find(el => el.visible && !el.closest('[inert]')) ??
            null)
          : null;
      if (target === this.#noticeTarget) return;
      this.#noticeObserver.disconnect();
      clearTimeout(this.#noticeTimer);
      this.#noticeTarget = target;
      if (target) this.#noticeObserver.observe(target);
    });
  }

  #onNoticeIntersect(entries) {
    const entry = entries
      .filter(item => item.target === this.#noticeTarget)
      .at(-1);
    clearTimeout(this.#noticeTimer);
    if (!entry?.isIntersecting || entry.intersectionRatio < NOTICE_RATIO)
      return;
    this.#noticeTimer = setTimeout(() => {
      if (!this.isConnected) return;
      marketNotice.markSeen();
      this.#noticeObserver.disconnect();
      this.#noticeTarget = null;
    }, NOTICE_DWELL_MS);
  }

  #markStale() {
    this.#stale = true;
    if (this.#open) this.#refresh();
  }

  #refresh() {
    if (this.#refreshing) return;
    this.#refreshing = true;
    this.#stale = false;
    const shell = this.q('slide-cart');
    shell?.setLoading?.(true);
    this.#mutate(() => cart.refresh(), { notify: this.#open })
      .catch(() => {
        this.#stale = true;
      })
      .finally(() => {
        this.#refreshing = false;
        if (this.isConnected) shell?.setLoading?.(false);
      });
  }

  #onPageShow(event) {
    this.#boundAll(CHECKOUT).forEach(el => el.setLoading(this.#busy > 0));
    if (event.persisted) this.#markStale();
  }

  #onClick(event) {
    const button = event.target.closest(CHECKOUT);
    if (!button?.control?.contains(event.target) || !isPlainClick(event))
      return;
    if (this.#busy) {
      event.preventDefault();
      return;
    }
    if (!this.#bound.has(button)) return;
    button.setLoading(true);
    clearTimeout(this.#checkoutTimer);
    this.#checkoutTimer = setTimeout(() => {
      if (this.isConnected) button.setLoading(false);
    }, CHECKOUT_RESET_MS);
  }

  #onMediaError(event) {
    const target = event.target;
    if (target.matches?.('.slide-cart-card__img')) target.hidden = true;
  }
}

if (!customElements.get('cart-drawer'))
  customElements.define('cart-drawer', CartDrawer);
